import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/navigation';

import { PrizeEntryItem } from '@flipster/apis/v1/campaign/types';
import { authBunja } from '@flipster/features/src/shared/auth/state';
import { useOverlay } from '@flipster/overlay';
import { Button } from '@flipster/react/design-system';
import { css } from '@flipster/styled-system/css';
import { grid, vstack } from '@flipster/styled-system/patterns';
import { numberFormatter } from '@flipster/utils/num';

import { authRedirectBunja } from '@web/components/AuthRedirect/bunja';

import SubmitPrizeEntryModal from './submit-prize-entry-modal';

interface PrizeEntryBoardProps {
  items: PrizeEntryItem[];
  availableTickets: number;
  slug: string;
  drawStatus: 'UPCOMING' | 'ONGOING' | 'FINISHED';
}

const PrizeEntryBoard = ({ items, availableTickets, slug, drawStatus }: PrizeEntryBoardProps) => {
  const router = useRouter();
  const { isLoggedInAtom } = useBunja(authBunja);
  const { setReferrerPath } = useBunja(authRedirectBunja);
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const overlay = useOverlay();

  const openSubmitPrizeEntryModal = (item: PrizeEntryItem) => {
    if (!isLoggedIn) {
      setReferrerPath();
      router.push('/signup');
      return;
    }
    overlay.open(<SubmitPrizeEntryModal item={item} availableTickets={availableTickets} slug={slug} />);
  };

  return (
    <div
      className={css({
        w: '100%',
        p: { base: 0, tablet: 20 },
        rounded: 40,
        bgColor: 'background.accent.primary',
      })}
    >
      <ul
        className={grid({
          gridTemplateColumns: { base: 'repeat(1, 1fr)', tablet: 'repeat(3, 1fr)' },
          gap: { base: 12, tablet: 24 },
          p: { base: 0, tablet: 20 },
          rounded: 28,
          bgColor: { base: 'background.default.level-0', tablet: 'background.default.level-1' },
        })}
      >
        {items.map((item) => (
          <li
            key={item.id}
            className={vstack({
              gap: 12,
              py: 24,
              px: 16,
              rounded: 28,
              bgColor: 'background.default.level-2',
            })}
          >
            <div className={vstack({ gap: 8, minH: { base: 87, tablet: 92 } })}>
              <p
                className={css({
                  textStyle: 'Display/L',
                  color: 'content.accent.promotion',
                  textAlign: 'center',
                })}
              >
                {item.name}
              </p>
              {item.description && (
                <p
                  className={css({
                    textStyle: 'Title/XS',
                    color: 'content.accent.promotion',
                    textAlign: 'center',
                  })}
                >
                  {item.description}
                </p>
              )}
              <span
                className={css({
                  textStyle: 'Title/XS',
                  color: 'content.default.level-4',
                  textAlign: 'center',
                })}
              >
                Total entries: {numberFormatter(item.totalEntries)}
              </span>
            </div>
            <div
              className={css({
                mx: 14,
                mt: 'auto',
                aspectRatio: 1 / 1,
                maxW: { base: 120, tablet: 300 },
                maxH: { base: 120, tablet: 300 },
              })}
            >
              <img src={item.image} alt={item.name} width='100%' />
            </div>
            <span
              className={css({
                textStyle: 'Title/XS',
                color: 'content.default.level-4',
                textAlign: 'center',
              })}
            >
              Your draw entries: {isLoggedIn ? numberFormatter(item.yourEntries ?? 0) : '-'}
            </span>
            <Button
              hierarchy='primary'
              size='large'
              color='neutral'
              label='Submit entry'
              disabled={drawStatus !== 'ONGOING'}
              onClick={() => openSubmitPrizeEntryModal(item)}
            />
          </li>
        ))}
      </ul>
    </div>
  );
};

export default PrizeEntryBoard;
